// Helper functions - exported
let noop = () => {},
    identity = (x) => x,
    nonNil = (x) => x != null,
    messageIsEmpty = (message) => !message || !message.response,
    messageIsArray = (message) => Array.isArray(message.response),
    // Just check valid types to see if this message's type is in it.
    messageTypeIsValid = (message, validTypes=defaultValidRequestTypes) => {
        if (messageIsEmpty(message)) {
            return false;
        }
        return validTypes.indexOf(message.messageType) !== -1;
    },
    // Return response actions from message
    getResponseActions = (response) => {
        if (!response || !response.responseActions) return [];
        const responseActions = Array.isArray(response.responseActions) ? 
            response.responseActions : [];
        return responseActions.filter(nonNil);
    },
    // Return all response actions from the message with the given name(s)
    getResponseActionsNamed = (response, names) => {
        if (!names) return [];
        if (!Array.isArray(names)) names = [names];
        return names.reduce((actions, name) => [
            ...actions, 
            ...getResponseActions(response)
                .filter(ra => ra.Name === name || ra.name === name)
        ], [])
    },
    // Return the first response actions from the message with the given name
    getFirstResponseActionNamed = (response, name) => {
        return getResponseActionsNamed(response, name)[0];
    },
    // Find a matching response action within the message's response actions
    hasResponseAction = (response, name) => {
        let action = getFirstResponseActionNamed(response, name);
        return action != null;
    };

let defaultValidRequestTypes = [
    "AppeventRequest",
    "ConverseRequest",
    "RequestUnitRequest",
    "HistoryItem"
];

// When message.response is an array, we can process the individual responses.
let defaultProcessArray = (message, isResponseValid, processSingle) => {
    let processResponse = response => isResponseValid(response) ?
                                      processSingle(response) : response;
    return Object.assign({}, message,
                         { response: message.response.map(processResponse) });
}

// Check whether response is non-empty and has action with name, if specified
let defaultIsResponseWithinArrayValid = (response, responseActionName) => {
    if (!response) {
        return false;
    } else if (responseActionName &&
               !hasResponseAction(response, responseActionName)) { 
        return false;
    }
    return true;
}

// Check whether message is non-empty, has a valid type,
// and has response action with name, if specified
let defaultIsSingleMessageValid = (message, validTypes, responseActionName) => {
    if (messageIsEmpty(message)) {
        return false;
    } else if (!messageTypeIsValid(message, validTypes)) {
        return false;
    } else if (responseActionName &&
               !hasResponseAction(message.response, responseActionName)) { 
        return false;
    }
    return true;
}

// Validate response, then process if valid.
let defaultProcessValidSingle = (message, isValid, processSingle) => {
    if (!isValid(message)) {
        return message;
    }
    let response = processSingle(message.response);
    let result = Object.assign({}, message, { response });
    return result;
}

// Define the options that we can, here
let defaultOptions = { responseActionName: null,
    validTypes: defaultValidRequestTypes, processSingle: identity };

// Put this in a separate function (SRP)
let processOptions = (options={}) => {
    // Must be defined within closure to refer to options 
    let isSingleMessageValid = (message) => 
            defaultIsSingleMessageValid(message, options.validTypes, 
                                        options.responseActionName),
        isResponseWithinArrayValid = (response) => 
            defaultIsResponseWithinArrayValid(response, 
                                              options.responseActionName),
        processValidSingle = (message) => 
            defaultProcessValidSingle(message, options.isSingleMessageValid, 
                                      options.processSingle),
        processArray = (message) => 
            defaultProcessArray(message, options.isResponseWithinArrayValid,
                                options.processSingle);

    let defaultOptionsMethods = { isSingleMessageValid, isResponseWithinArrayValid,
                                  processValidSingle, processArray }
    // Now combine them.
    options = Object.assign({}, defaultOptions, defaultOptionsMethods, options);
    return options;
}

// This is the function that most other modules will want to use.
// Pass it the options for overrides along with the message to process.
// All you really have to pass it is your processSingle function, and
// for most cases it'll take care of the rest. Also the responseActionName,
// if you're using those.
let processResponse = (message, options={}) => { 
    if (messageIsEmpty(message)) { 
        return message;
    }
    options = processOptions(options);
    
    let isArray = messageIsArray(message)
    let result = isArray ? 
        options.processArray(message, options.processSingle) :
        options.processValidSingle(message);
    return result;
}

export {
    messageIsEmpty,
    messageIsArray,
    messageTypeIsValid,
    getResponseActions,
    getResponseActionsNamed,
    getFirstResponseActionNamed,
    hasResponseAction,
    processResponse
}


// WEBPACK FOOTER //
// ./node_modules/@nextit/nextit-lib-response-handler/lib/lib-response-handler.js